/**
 * Lista de clientes do Gerenciador de Produção (redesign 29/10/2026):
 * indicadores, Leitura do Analista, saúde da carteira, próximas entregas,
 * abas de situação, filtros e a tabela ordenada por risco.
 */
import React, { useMemo, useRef, useState } from "react";
import { FiSearch, FiX } from "react-icons/fi";
import { useNavigate } from "react-router-dom";

import BotaoExportacao from "../../../../components/Exportacao";
import { useDados } from "../../../../context/DadosContext";
import { usePrecosVisiveis } from "../../../../context/PrecosContext";
import { useExportSnapshot } from "../../../../hooks/useExportSnapshot";
import { compararTexto, SITUACOES } from "../dados/montarClientes";
import { analisarCarteira } from "../dados/carteira";
import { seloDaSincronizacao } from "../dados/sincronizacao";
import {
  FILTROS_AVANCADOS_INICIAIS,
  FILTROS_DA_CENTRAL,
  contarPorFiltroRapido,
  filtrarLinhas,
  ordenarLinhas,
} from "../dados/filtros";
import useCentralDaCarteira from "../dados/useCentralDaCarteira";
import { abrirFichaAoClicar, ultimoClienteAberto } from "../comum/transicao";
import { montarExportacao } from "./exportacao";
import Barra from "./Barra";
import FiltrosAvancados from "./FiltrosAvancados";
import Indicadores from "./Indicadores";
import { LeituraDoAnalista, ProximasEntregas, SaudeDaCarteira } from "./Leitura";
import TabelaDeClientes from "./TabelaDeClientes";
import useEstadoDaLista from "./useEstadoDaLista";

const ROTULO_DA_SITUACAO = {
  [SITUACOES.TODOS]: "clientes",
  [SITUACOES.PENDENTES]: "clientes pendentes",
  [SITUACOES.EM_ANDAMENTO]: "clientes em andamento",
  [SITUACOES.CONCLUIDOS]: "clientes concluídos",
};

function horaDaPlanilha(data) {
  if (!data) return "";
  const d = new Date(data);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" })} às ${d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}`;
}

/** De onde vêm os dados; sincronizar fica no cabeçalho global ("Atualizado ..."). */
function Sincronizacao() {
  const dados = useDados();
  const quando = horaDaPlanilha(dados.atualizadoEm);
  const selo = seloDaSincronizacao(dados);
  return (
    <p className="cl-sub">
      <span className={`cl-live${selo.desligado ? " off" : ""}`}>{selo.rotulo}</span>
      <span aria-hidden="true">·</span>
      <span>Gerenciador de Produção{quando ? ` · atualizado ${quando}` : ""}</span>
    </p>
  );
}

function Busca({ valor, aoMudar, campoRef }) {
  return (
    <label className="cl-search">
      <FiSearch size={16} aria-hidden="true" />
      <input
        ref={campoRef}
        type="search"
        aria-label="Buscar cliente, pedido, lote ou projetista"
        placeholder="Cliente, pedido, projetista…"
        value={valor}
        onChange={(e) => aoMudar(e.target.value)}
      />
      {valor ? (
        <button type="button" className="cl-limpar" aria-label="Limpar busca" onClick={() => aoMudar("")}>
          <FiX size={14} />
        </button>
      ) : (
        <kbd aria-hidden="true">/</kbd>
      )}
    </label>
  );
}

export default function ListaDeClientes({ clientes, animarEntrada = true }) {
  const navigate = useNavigate();
  const { precosVisiveis } = usePrecosVisiveis() || {};
  const central = useCentralDaCarteira();
  const comCentral = Boolean(central.pedidos);
  const estado = useEstadoDaLista();
  const [avancados, setAvancados] = useState(FILTROS_AVANCADOS_INICIAIS);
  const campoDaBusca = useRef(null);
  const paginaRef = useRef(null);

  const carteira = useMemo(() => analisarCarteira(clientes, central.pedidos), [clientes, central.pedidos]);
  const rapido = !comCentral && FILTROS_DA_CENTRAL.has(estado.rapido) ? "" : estado.rapido;

  const linhas = useMemo(() => {
    const filtradas = filtrarLinhas(carteira.linhas, {
      situacao: estado.situacao, rapido, busca: estado.buscaAplicada, avancados,
    });
    return ordenarLinhas(filtradas, { ordem: estado.ordem, direcao: estado.direcao });
  }, [carteira.linhas, estado.situacao, rapido, estado.buscaAplicada, avancados, estado.ordem, estado.direcao]);

  const contagemRapidos = useMemo(
    () => contarPorFiltroRapido(carteira.linhas, estado.situacao),
    [carteira.linhas, estado.situacao],
  );
  const vendedores = useMemo(() => [...new Set(clientes.flatMap((c) => c.vendedores))].sort(compararTexto), [clientes]);
  const consultores = useMemo(() => [...new Set(clientes.flatMap((c) => c.consultores))].sort(compararTexto), [clientes]);

  const exportacao = useMemo(
    () => montarExportacao({ linhas, carteira, comCentral, paginaRef }),
    [linhas, carteira, comCentral],
  );
  useExportSnapshot("clientes", exportacao, paginaRef);

  const aoAbrir = useMemo(() => abrirFichaAoClicar(navigate), [navigate]);
  const destacadaId = useMemo(() => ultimoClienteAberto(), []);

  function limparTudo() {
    estado.limpar();
    setAvancados(FILTROS_AVANCADOS_INICIAIS);
  }

  return (
    <div className="clg" ref={paginaRef}>
      <nav className="cl-crumb" aria-label="Caminho">
        <span>Gerenciador de Produção</span>
        <span aria-hidden="true">›</span>
        <b>Clientes</b>
      </nav>
      <header className="cl-head">
        <div>
          <h1 className="cl-h1">Clientes</h1>
          <Sincronizacao />
        </div>
        <div className="cl-acts">
          <Busca valor={estado.busca} aoMudar={estado.setBusca} campoRef={campoDaBusca} />
          <FiltrosAvancados
            filtros={avancados}
            aoMudar={(campo, valor) => setAvancados((atual) => ({ ...atual, [campo]: valor }))}
            aoLimpar={() => setAvancados(FILTROS_AVANCADOS_INICIAIS)}
            vendedores={vendedores}
            consultores={consultores}
            visiveis={linhas.length}
            total={carteira.linhas.length}
          />
          <div className="cl-exportar">
            <BotaoExportacao
              titulo="Clientes"
              resumo={exportacao.resumo}
              secoes={exportacao.secoes}
              visual={exportacao.visual}
              label="Exportar"
            />
          </div>
        </div>
      </header>

      <div className="cl-lgrid">
        <Indicadores indicadores={carteira.indicadores} precosVisiveis={precosVisiveis} />
        <div className="cl-ins">
          <LeituraDoAnalista
            leitura={carteira.leitura}
            comCentral={comCentral}
            rapido={rapido}
            aoFiltrar={estado.alternarRapido}
            aoAbrir={aoAbrir}
          />
          <SaudeDaCarteira saude={carteira.saude} esperas={carteira.esperas} />
          <ProximasEntregas entregas={carteira.proximasEntregas} aoAbrir={aoAbrir} />
        </div>
        <Barra
          situacao={estado.situacao}
          porSituacao={carteira.porSituacao}
          aoTrocarSituacao={estado.setSituacao}
          rapido={rapido}
          aoFiltrar={estado.alternarRapido}
          contagemRapidos={contagemRapidos}
          comCentral={comCentral}
        />
        <TabelaDeClientes
            linhas={linhas}
            situacaoRotulo={ROTULO_DA_SITUACAO[estado.situacao]}
            ordem={estado.ordem}
            direcao={estado.direcao}
            aoOrdenar={estado.ordenarPor}
            comCentral={comCentral}
            precosVisiveis={precosVisiveis}
            aoAbrir={aoAbrir}
            destacadaId={destacadaId}
            aoLimpar={limparTudo}
            animarEntrada={animarEntrada}
          />
      </div>
    </div>
  );
}
